'use client';

import { Banner, BannerClickAction } from '@medprev-web-app/api-graphql-client';
import { useParams, useRouter } from 'next/navigation';
import { useState } from 'react';
import { useStore } from 'zustand';

import { useNavigation } from '@/hooks/navigation/use-navigation.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { SearchModalOriginEnum } from '@/types/enums/analytics/home/home-analytics.enum';
import { userLocationStore } from '@/store/user-location.store';
import { handleOpenSearchModal } from '@/store/search/handle-open-search-modal';
import { resolveBannerClickAction } from '@/app/(main)/(pages)/_utils/banner-click-action.util';

interface BannerSheetProps {
  modalTitle: string | null;
  modalDescription: string | null;
  modalButtonText: string | null;
  onConfirm: () => void;
}

export function useBannerSheet() {
  const router = useRouter();
  const { resolveHref } = useNavigation();
  const analytics = getAnalyticsPlugin();
  const { slug } = useParams();
  const userLocation = useStore(
    userLocationStore,
    (state) => state.userLocation,
  );

  const [isBannerSheetOpen, setBannerSheetOpen] = useState(false);
  const [bannerSheetProps, setBannerSheetProps] = useState<BannerSheetProps>({
    modalTitle: '',
    modalDescription: null,
    modalButtonText: null,
    onConfirm: () => {},
  });

  const handleTrackAnalytics = (item: Banner) => {
    const isInCityPage = Boolean(slug);

    analytics.track(AnalyticsEnum.CLICK_BANNER, {
      banner_id: item.id,
      banner_nome: item.modalTitle,
      pagina: isInCityPage ? 'cidade' : 'home',
      cidade: userLocation?.city ? `${userLocation.city}-${userLocation.state}` : undefined,
    });
  };

  const handleBannerClick = (item: Banner) => {
    handleTrackAnalytics(item);

    const {
      modalTitle,
      modalDescription = '',
      modalButtonText = '',
      actionModalButton,
      actionOnBannerClick,
    } = item;
    const clickAction = resolveBannerClickAction(item);

    if (clickAction === BannerClickAction.Search) {
      handleOpenSearchModal({ origin: SearchModalOriginEnum.BANNER, openAfterLocationSelection: true });
      return;
    }

    if (clickAction === BannerClickAction.Url && actionOnBannerClick) {
      router.push(resolveHref(actionOnBannerClick));
      return;
    }

    if (clickAction !== BannerClickAction.Modal || !modalTitle) {
      return;
    }

    setBannerSheetOpen(true);
    setBannerSheetProps({
      modalTitle,
      modalDescription,
      modalButtonText,
      onConfirm: () => {
        if (actionModalButton) {
          router.push(resolveHref(actionModalButton));
        } else {
          setBannerSheetOpen(false);
        }
      },
    });
  };

  return {
    isBannerSheetOpen,
    setBannerSheetOpen,
    bannerSheetProps,
    handleBannerClick,
  };
}
